import { AlertTriangle, Clock, ChevronRight, User, MapPin } from 'lucide-react';

const STATUS_LABELS = {
  in_warehouse: 'En Bodega',
  in_fastmax:   'En FastMax',
  with_driver:  'Con Conductor',
};

const STATUS_COLORS = {
  in_warehouse: 'text-amber-600 bg-amber-50',
  in_fastmax:   'text-blue-600 bg-blue-50',
  with_driver:  'text-purple-600 bg-purple-50',
};

/**
 * StuckPackagesAlert
 *
 * Displays packages that have been in the same active status
 * for more than STUCK_THRESHOLD_HOURS (12h).
 * Oldest/most stuck packages appear first.
 * Clicking a row opens its history panel.
 */
export function StuckPackagesAlert({ stuckPackages, threshold, loading, onPackageClick }) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-orange-200 overflow-hidden">
        <AlertHeader count={0} loading />
        <div className="p-4 space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const hasAlerts = stuckPackages && stuckPackages.length > 0;

  return (
    <div className={`bg-white rounded-xl border overflow-hidden ${hasAlerts ? 'border-orange-200' : 'border-gray-200'}`}>
      <AlertHeader count={stuckPackages?.length ?? 0} threshold={threshold} />

      {!hasAlerts ? (
        <div className="px-4 py-6 text-center">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Clock size={18} className="text-brand-green" />
          </div>
          <p className="font-montserrat text-sm text-gray-500">
            Sin paquetes detenidos. ¡Todo fluye bien!
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
          {stuckPackages.map(pkg => {
            const statusColor = STATUS_COLORS[pkg.status] || 'text-gray-600 bg-gray-50';
            const statusLabel = STATUS_LABELS[pkg.status] || pkg.status;

            // Urgency visual cue: >24h is critical, 12-24h is warning
            const isCritical = pkg.hours_stuck >= 24;

            return (
              <button
                key={pkg.id}
                onClick={() => onPackageClick(pkg.id)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50/50 transition-colors text-left group"
              >
                {/* Urgency indicator dot */}
                <div className={`shrink-0 w-2 h-2 rounded-full ${isCritical ? 'bg-red-500' : 'bg-orange-400'}`} />

                {/* Package info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-montserrat font-bold text-sm text-gray-800">
                      {pkg.delivery_point_id}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-montserrat font-semibold ${statusColor}`}>
                      {statusLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {pkg.recipient_name && (
                      <span className="text-xs text-gray-500 font-montserrat flex items-center gap-1">
                        <User size={10} />
                        {pkg.recipient_name}
                      </span>
                    )}
                    {pkg.district && (
                      <span className="text-xs text-gray-500 font-montserrat flex items-center gap-1">
                        <MapPin size={10} />
                        {pkg.district}
                      </span>
                    )}
                  </div>
                </div>

                {/* Hours stuck badge */}
                <div className="shrink-0 text-right">
                  <span className={`font-montserrat font-bold text-sm ${isCritical ? 'text-red-600' : 'text-orange-500'}`}>
                    {pkg.hours_stuck}h
                  </span>
                  <p className="text-xs text-gray-400 font-montserrat">detenido</p>
                </div>

                <ChevronRight size={14} className="shrink-0 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AlertHeader({ count, threshold, loading }) {
  return (
    <div className={`flex items-center gap-2 px-4 py-3 border-b ${count > 0 ? 'bg-orange-50 border-orange-100' : 'bg-gray-50 border-gray-100'}`}>
      <AlertTriangle
        size={16}
        className={count > 0 ? 'text-orange-500' : 'text-gray-400'}
      />
      <span className="font-montserrat font-bold text-sm text-gray-700 flex-1">
        Paquetes Detenidos
      </span>
      {!loading && (
        <div className="flex items-center gap-2">
          {count > 0 && (
            <span className="bg-orange-500 text-white text-xs font-montserrat font-bold px-2 py-0.5 rounded-full">
              {count}
            </span>
          )}
          {threshold && (
            <span className="text-xs text-gray-400 font-montserrat">
              +{threshold}h sin cambio
            </span>
          )}
        </div>
      )}
    </div>
  );
}